import { type Person } from '@clubspark-node/clubspark-tennis-types';

import { type StaffData } from 'components/tables/staffTable/types';
import { getClientConfig } from 'config/clients';
import { client } from 'config/graphql/client';
import { DEMO_MODE_INFO_MESSAGE } from 'config/graphql/errors';
import { COMBINED_ELIGIBILITY, GET_PLAYER_PROFILE, PERSON_EVENT_ELIGIBILITY } from 'config/graphql/queries';
import { ADD_TOURNAMENT_ASSISTANTS, GET_STAFF_USERS, REMOVE_TOURNAMENT_ASSISTANT } from 'config/graphql/staffQueries';
import { CloudError } from 'repository/CloudError';
import { NOT_IMPLEMENTED } from 'repository/messages';
import type Repository from 'repository/repository';
import { type CloudResult, type RepositoryCacheType } from 'repository/repository';
import { type CombinedEventEligibility, EligibilityStatus, type EventEligibility } from 'typedefs/event';
import { isStandalone } from 'utils/environment';

export interface PersonRepository extends Repository<Person> {
  getPersonEventEligibility(
    personCsIds: string[],
    tournamentId: string,
    eventId: string,
    cache?: RepositoryCacheType,
  ): Promise<CloudResult<EventEligibility>>;
  getPersonCombinedEventEligibility(
    participantIdPairs: string[][],
    eventId: string,
    cache?: RepositoryCacheType,
  ): Promise<CloudResult<CombinedEventEligibility>>;
  getTournamentStaff(id: string): Promise<CloudResult<StaffData[]>>;
  saveAssistant(tournamentId: string, userId: string): Promise<CloudResult<StaffData>>;
  removeAssistant(tournamentId: string, userId: string): Promise<CloudResult<StaffData>>;
}

const { ELIGIBLE } = EligibilityStatus;

export class PersonRepositoryImpl implements PersonRepository {
  async get(
    personId: string,
    tournamentId: string,
    cache: RepositoryCacheType = 'cache-first',
  ): Promise<CloudResult<Person>> {
    if (isStandalone()) {
      return Promise.resolve({ error: new CloudError('demo-mode-query', DEMO_MODE_INFO_MESSAGE) });
    }

    const graphqlResult = await client.query<Person>({
      query: GET_PLAYER_PROFILE,
      fetchPolicy: cache,
      variables: { personId, tournamentId },
    });

    if (graphqlResult?.error) {
      return Promise.resolve({ error: new CloudError(graphqlResult?.error.name, graphqlResult?.error.message) });
    }

    return Promise.resolve({ data: graphqlResult.data });
  }

  async getPersonEventEligibility(
    personCsIds: string[],
    tournamentId: string,
    eventId: string,
    cache: RepositoryCacheType = 'cache-first',
  ): Promise<CloudResult<EventEligibility>> {
    if (isStandalone()) {
      // in case of demo mode just skip the check without returning the error, so participants pairing can work
      return Promise.resolve({
        data: {
          personEventEligibility: [
            {
              eligibilityRuleStatuses: [
                {
                  ruleId: '1',
                  status: ELIGIBLE,
                  strictlyEnforce: false,
                },
              ],
              eligibilityStatus: ELIGIBLE,
            },
          ],
        },
      });
    }
    const graphqlResult = await client.query<EventEligibility>({
      query: PERSON_EVENT_ELIGIBILITY,
      fetchPolicy: cache,
      variables: { personCsIds, tournamentId, eventId },
    });

    if (graphqlResult?.error) {
      return Promise.resolve({ error: new CloudError(graphqlResult?.error.name, graphqlResult?.error.message) });
    }

    return Promise.resolve({ data: graphqlResult.data });
  }

  async getPersonCombinedEventEligibility(
    participantIdPairs: string[][],
    eventId: string,
    cache: RepositoryCacheType = 'cache-first',
  ): Promise<CloudResult<CombinedEventEligibility>> {
    if (isStandalone()) {
      // in case of demo mode just skip the check without returning the error, so participants pairing can work
      return Promise.resolve({
        data: {
          combineParticipantsEligibility: [
            {
              participantIdPair: ['demo', 'demo'],
              success: true,
              error: false,
            },
          ],
        },
      } as unknown as CloudResult<CombinedEventEligibility>);
    }

    const graphqlResult = await client.query<CombinedEventEligibility>({
      query: COMBINED_ELIGIBILITY,
      fetchPolicy: cache,
      variables: { participantIdPairs, eventId },
    });

    if (graphqlResult?.error) {
      return Promise.resolve({ error: new CloudError(graphqlResult?.error.name, graphqlResult?.error.message) });
    }

    return Promise.resolve({ data: graphqlResult.data });
  }

  async getTournamentStaff(id: string, cache?: RepositoryCacheType): Promise<CloudResult<StaffData[]>> {
    if (isStandalone()) {
      return Promise.resolve({ error: new CloudError('demo-mode-query', DEMO_MODE_INFO_MESSAGE) });
    }

    const graphqlResult = await client.query<{ tournamentStaffList: StaffData[] }>({
      query: GET_STAFF_USERS,
      fetchPolicy: cache,
      variables: { clubSparkTournamentId: id },
    });

    if (graphqlResult?.error) {
      return Promise.resolve({ error: new CloudError(graphqlResult?.error.name, graphqlResult?.error.message) });
    }
    return Promise.resolve({ data: graphqlResult.data.tournamentStaffList });
  }

  save(item: Person) {
    console.log(item);
    return Promise.resolve({ error: NOT_IMPLEMENTED });
  }

  async saveAssistant(tournamentId: string, userId: string): Promise<CloudResult<StaffData>> {
    // this action is not available in standalone mode (for now)
    if (isStandalone()) {
      return Promise.resolve({ error: new CloudError('demo-mode-query', DEMO_MODE_INFO_MESSAGE) });
    }

    const mutationResult = await client.mutate({
      mutation: ADD_TOURNAMENT_ASSISTANTS,
      variables: {
        clubSparkTournamentId: tournamentId,
        uaid: userId,
        legacyMode: getClientConfig().legacyAssistantSearch,
      },
    });

    if (mutationResult?.errors) {
      return Promise.resolve({
        error: new CloudError(mutationResult?.errors[0].name, mutationResult?.errors[0].message),
      });
    }

    return Promise.resolve({ data: mutationResult.data });
  }
  async removeAssistant(tournamentId: string, userId: string): Promise<CloudResult<StaffData>> {
    // this action is not available in standalone mode (for now)
    if (isStandalone()) {
      return Promise.resolve({ error: new CloudError('demo-mode-query', DEMO_MODE_INFO_MESSAGE) });
    }

    const mutationResult = await client.mutate({
      mutation: REMOVE_TOURNAMENT_ASSISTANT,
      variables: {
        clubSparkTournamentId: tournamentId,
        id: userId,
      },
    });

    if (mutationResult?.errors) {
      return Promise.resolve({
        error: new CloudError(mutationResult?.errors[0].name, mutationResult?.errors[0].message),
      });
    }

    return Promise.resolve({ data: mutationResult.data });
  }
}
